import React, { useState, useEffect } from 'react';
import { getAllContactMessages, deleteContactMessage, markContactMessageAsRead } from '../../utils/firestore';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Modal, Button } from 'antd';

const ManageContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const messagesData = await getAllContactMessages();
      setMessages(messagesData);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      toast.error('Mesajlar yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleViewMessage = async (message) => {
    try {
      // Mark as read when viewing
      if (!message.read) {
        await markContactMessageAsRead(message.id);
        // Update local state
        setMessages(prev => 
          prev.map(m => m.id === message.id ? { ...m, read: true, status: 'read' } : m)
        );
      }
      setSelectedMessage(message);
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error marking message as read:', error);
      toast.error('Mesaj durumu güncellenirken hata oluştu');
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteContactMessage(id);
      setMessages(prev => prev.filter(m => m.id !== id));
      toast.success('Mesaj başarıyla silindi');
      if (selectedMessage && selectedMessage.id === id) {
        setIsModalVisible(false);
        setSelectedMessage(null);
      }
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Mesaj silinirken hata oluştu');
    }
    setConfirmDelete(null);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (message) => {
    if (message.read) {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
          Okundu
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
          Okunmadı
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-nihal-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">İletişim Mesajları</h1>
        <Button 
          type="primary" 
          onClick={fetchMessages}
          className="bg-nihal-primary border-nihal-primary"
        >
          Yenile
        </Button>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Mesaj bulunamadı</h3>
          <p className="text-gray-500">Yeni iletişim mesajları burada görünecek</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durum
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ad Soyad
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mail
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tarih
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Eylemler
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {messages.map((message, index) => (
                  <motion.tr
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className={message.read ? 'bg-white' : 'bg-blue-50 hover:bg-blue-100'}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(message)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{message.name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{message.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(message.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Button 
                        type="link" 
                        size="small" 
                        onClick={() => handleViewMessage(message)}
                        className="text-nihal-primary hover:text-nihal-secondary"
                      >
                        Görüntüle
                      </Button>
                      <Button 
                        type="link" 
                        size="small" 
                        danger
                        onClick={() => setConfirmDelete(message.id)}
                        className="ml-2"
                      >
                        Sil
                      </Button>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message Detail Modal */}
      <Modal
        title={`Mesaj Detayı ${selectedMessage?.read ? '' : '(Okunmadı)'}`}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setIsModalVisible(false)}>
            Kapat
          </Button>,
          <Button 
            key="delete" 
            danger 
            onClick={() => setConfirmDelete(selectedMessage?.id)}
          >
            Sil
          </Button>
        ]}
      >
        {selectedMessage && (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-gray-500">Ad Soyad</h3>
              <p className="mt-1 text-sm text-gray-900">{selectedMessage.name}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Mail</h3>
              <p className="mt-1 text-sm text-gray-900">{selectedMessage.email}</p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Tarih</h3>
              <p className="mt-1 text-sm text-gray-900">
                {formatDate(selectedMessage.createdAt)}
              </p>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500">Mesaj</h3>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
                {selectedMessage.message}
              </p>
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Silme Onayı"
        open={confirmDelete !== null}
        onCancel={() => setConfirmDelete(null)}
        onOk={() => handleDelete(confirmDelete)}
        okText="Sil"
        cancelText="İptal"
        okButtonProps={{ danger: true }}
      >
        Bu mesajı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
      </Modal>
    </div>
  );
};

export default ManageContactMessages;